/* eslint-disable no-console */

/**
 * hamburgerMenu.js
 * 
 * Dedicated module for creating and managing hamburger menu navigation
 * for both paginated and non-paginated contexts
 * Fixed focus highlight issue when clicking hamburger button
 */

import { debugLog } from './index';
import {
  isInPreviewMode,
  clearPreviewSession,
  PREVIEW_PARAM_KEYS,
} from './previewParams';

/**
 * Creates an integrated hamburger menu for navigation
 * 
 * @param {Object} config - Configuration object
 * @param {Array} config.headings - Array of heading objects with { element, level, id, text }
 * @param {Element} config.container - Container element for scrolling calculations
 * @param {Object} config.styles - Additional styles to apply
 * @param {string} config.context - Context identifier ('paginated', 'preview', etc.)
 * @returns {Object} Created hamburger menu element and control functions
 */
/**
 * Returns true when the hamburger menu should NOT be created:
 * - Print media query is active (browser print dialog / print preview)
 * - Running inside headless Chrome used for PDF generation
 *   · "HeadlessChrome" in the UA covers older headless Chrome builds (pre-112)
 *   · navigator.webdriver removed: causes false positives with --remote-debugging-port;
 *     headless PDF generation already identified by print media query + HeadlessChrome UA
 */
function shouldSkipHamburger() {
  if (typeof window === 'undefined') return true;
  if (window.matchMedia && window.matchMedia('print').matches) return true;
  if (/HeadlessChrome/.test(navigator.userAgent)) return true;
  return false;
}

function createHamburgerMenu(config) {
  const {
    headings = [],
    container = document.body,
    styles = {},
    context = 'default',
    manuals = [],
    currentManualId = null,
    currentSiteId = null,
  } = config;
  // Store context in navData so createExpandableContent can use it

  if (shouldSkipHamburger()) {
    debugLog('Hamburger skipped: print or headless context');
    return null;
  }

  debugLog(`Creating hamburger menu with ${headings.length} headings in ${context} context`);

  if (headings.length === 0) {
    console.warn("No headings provided for hamburger menu");
    return null;
  }
  
  // Group headings by chapter (level 1) and topics (level 2)
  const chapterGroups = groupHeadingsByChapter(headings);
  
  debugLog(`Grouped headings into ${chapterGroups.length} chapters`);
  
  // Create the hamburger menu DOM structure
  const hamburgerContainer = createHamburgerMenuStructure(chapterGroups, {
    manuals,
    currentManualId,
    currentSiteId,
    context,
  });

  // Add menu to page
  document.body.appendChild(hamburgerContainer);
  
  // Set up event handlers and return control functions
  return setupHamburgerMenuEvents(hamburgerContainer);
}

/**
 * Groups headings by chapter structure
 * 
 * @param {Array} headings - Array of heading objects
 * @returns {Array} Array of chapter groups with topics
 */
function groupHeadingsByChapter(headings) {
  const chapterGroups = [];
  let currentChapter = null;
  
  headings.forEach(heading => {
    if (heading.level === 1) {
      // This is a chapter
      currentChapter = {
        heading: heading,
        topics: []
      };
      chapterGroups.push(currentChapter);
    } else if (heading.level === 2 && currentChapter) {
      // This is a topic, add it to the current chapter
      currentChapter.topics.push(heading);
    }
  });
  
  return chapterGroups;
}

/**
 * Creates the DOM structure for the hamburger menu
 * 
 * @param {Array} chapterGroups - Grouped chapter data
 * @returns {Element} Complete hamburger menu container
 */
function createHamburgerMenuStructure(chapterGroups, navData = {}) {
  // Create integrated hamburger menu container
  const hamburgerContainer = document.createElement("div");
  hamburgerContainer.id = "integrated-hamburger-menu";
  hamburgerContainer.classList.add("integrated-hamburger-menu");
  hamburgerContainer.dataset.context = navData.context || 'default';
  const isWebStaticCtx = navData.context === 'web-static';
  if (!isWebStaticCtx && isInPreviewMode()) hamburgerContainer.dataset.preview = 'true';

  // Create the header section
  const hamburgerHeader = createHamburgerHeader(navData);

  // Create expandable content area (navData already contains context)
  const expandableContent = createExpandableContent(chapterGroups, navData);

  // Assemble the complete menu
  hamburgerContainer.appendChild(hamburgerHeader);
  hamburgerContainer.appendChild(expandableContent);

  return hamburgerContainer;
}

/**
 * Creates the hamburger menu header with toggle button and title
 * 
 * @returns {Element} Header element
 */
function createHamburgerHeader(navData = {}) {
  const { context = 'default' } = navData;
  const isWebStatic = context === 'web-static';
  // Web-static pages are pre-rendered; never treat them as preview mode
  // even if sessionStorage has leftover preview params.
  const previewActive = !isWebStatic && isInPreviewMode();

  const hamburgerHeader = document.createElement("div");
  hamburgerHeader.classList.add("hamburger-header");
  
  // Create icon row container
  const iconRow = document.createElement("div");
  iconRow.classList.add("hamburger-icon-row");
  
  // Create hamburger toggle button
  const hamburgerBtn = document.createElement("button");
  hamburgerBtn.classList.add("hamburger-toggle-btn");
  hamburgerBtn.setAttribute('aria-label', 'Toggle Table of Contents');
  hamburgerBtn.innerHTML = `
    <svg class="hamburger-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <line x1="1" y1="6" x2="21" y2="6"></line>
      <line x1="1" y1="12" x2="21" y2="12"></line>
      <line x1="1" y1="18" x2="21" y2="18"></line>
    </svg>
  `;
  
  // Add hamburger button to icon row
  iconRow.appendChild(hamburgerBtn);

  // Home button — preview params are stored in sessionStorage, so the link
  // stays clean (no token in the URL).
  const homeBtn = document.createElement("a");
  homeBtn.href = "/";
  homeBtn.classList.add("hamburger-home-btn");
  homeBtn.setAttribute("aria-label", "Home");
  homeBtn.innerHTML = `
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  `;
  iconRow.appendChild(homeBtn);

  // Create title row — shows preview badge + exit button when in preview mode
  const titleRow = document.createElement("div");
  titleRow.classList.add("hamburger-title-row");

  if (previewActive) {
    titleRow.innerHTML = `
      <span class="menu-title preview-mode-title">
        <span class="preview-dot-inline"></span>LIVE PREVIEW
      </span>
      <button class="exit-preview-btn" title="Clear preview session and reload without preview params">Exit Preview</button>
    `;
    titleRow.querySelector('.exit-preview-btn').addEventListener('click', () => {
      clearPreviewSession();
      const url = new URL(window.location.href);
      PREVIEW_PARAM_KEYS.forEach(k => url.searchParams.delete(k));
      window.location.href = url.toString();
    });
  } else {
    titleRow.innerHTML = `<span class="menu-title">Table of Contents</span>`;
  }
  
  // Add rows to header
  hamburgerHeader.appendChild(iconRow);
  hamburgerHeader.appendChild(titleRow);

  // View-mode row: Web / PDF / Production — shown in preview mode when expanded,
  // or in web-static mode always (at ≥1200px it auto-unfurls).
  if (previewActive) {
    hamburgerHeader.appendChild(createViewModeRow());
  } else if (isWebStatic) {
    hamburgerHeader.appendChild(createWebStaticViewRow(navData));
  }

  return hamburgerHeader;
}

/**
 * Creates the view-mode icon row shown in the hamburger header for web-static pages.
 * Shows Web (active), PDF download, DigiGuide app link, and paginated/prod view link.
 * Hidden when collapsed; shown when expanded (or always visible at ≥1200px via CSS).
 */
function createWebStaticViewRow(navData = {}) {
  const { currentManualId, currentSiteId } = navData;
  const pdfBase = (typeof process !== 'undefined' && process.env && process.env.GATSBY_PDF_BASE_URL) || '';

  const row = document.createElement('div');
  row.classList.add('hamburger-view-row');

  // ── Web (current page — active) ────────────────────────────────────
  const webBtn = document.createElement('a');
  webBtn.href = window.location.pathname;
  webBtn.classList.add('view-mode-btn', 'active');
  webBtn.setAttribute('title', 'Web view (current)');
  webBtn.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect x="2" y="4" width="20" height="14" rx="2"/>
      <line x1="8" y1="22" x2="16" y2="22"/>
      <line x1="12" y1="18" x2="12" y2="22"/>
    </svg>
    <span>Web</span>
  `;

  // ── PDF download ───────────────────────────────────────────────────
  const pdfBtn = document.createElement('a');
  if (pdfBase && currentManualId && currentSiteId) {
    pdfBtn.href = `${pdfBase}/DCI_DigiGuide_Manual_${currentManualId}_Site_${currentSiteId}.pdf`;
    pdfBtn.target = '_blank';
    pdfBtn.rel = 'noreferrer';
  }
  pdfBtn.classList.add('view-mode-btn');
  pdfBtn.setAttribute('title', 'Download PDF');
  pdfBtn.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="8" y1="13" x2="16" y2="13"/>
      <line x1="8" y1="17" x2="13" y2="17"/>
    </svg>
    <span>PDF</span>
  `;

  // ── DigiGuide app deep link ────────────────────────────────────────
  const appBtn = document.createElement('a');
  if (currentManualId) {
    appBtn.href = `dci-digiguide:///link/Manual?id=${currentManualId}`;
  }
  appBtn.classList.add('view-mode-btn');
  appBtn.setAttribute('title', 'Open in DigiGuide App');
  appBtn.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
      <line x1="12" y1="18" x2="12.01" y2="18"/>
    </svg>
    <span>App</span>
  `;

  row.appendChild(webBtn);
  row.appendChild(pdfBtn);
  row.appendChild(appBtn);
  return row;
}

/**
 * Builds a URL for the current page with only `web` and `production` swapped.
 * Tokens are intentionally excluded — they live in sessionStorage.
 */
function buildViewUrl({ web, production }) {
  const url = new URL(window.location.href);
  // Strip secrets — they live in sessionStorage, not links
  ['token', 'x-craft-live-preview', 'x-craft-preview', 'api-url'].forEach(k => url.searchParams.delete(k));
  url.searchParams.delete('web');
  url.searchParams.delete('production');
  if (web === '0') url.searchParams.set('web', '0');     // web=1 is the default; omit it
  if (production)  url.searchParams.set('production', '1');
  return url.toString();
}

/**
 * Creates the view-mode toggle row shown in the header when in preview mode.
 * Web ↔ PDF are mutually exclusive; Production view is an independent toggle.
 * Navigates to the same page path with only the view params changed.
 */
function createViewModeRow() {
  const search = new URLSearchParams(window.location.search);
  const isWebMode  = search.get('web') !== '0';          // default: web mode
  const isProdMode = search.get('production') === '1';

  const row = document.createElement('div');
  row.classList.add('hamburger-view-row');

  // ── Web button ──────────────────────────────────────────────────────
  const webBtn = document.createElement('a');
  webBtn.href = buildViewUrl({ web: '1', production: isProdMode });
  webBtn.classList.add('view-mode-btn');
  if (isWebMode) webBtn.classList.add('active');
  webBtn.setAttribute('title', 'Web view');
  webBtn.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect x="2" y="4" width="20" height="14" rx="2"/>
      <line x1="8" y1="22" x2="16" y2="22"/>
      <line x1="12" y1="18" x2="12" y2="22"/>
    </svg>
    <span>Web</span>
  `;

  // ── PDF button ──────────────────────────────────────────────────────
  const pdfBtn = document.createElement('a');
  pdfBtn.href = buildViewUrl({ web: '0', production: isProdMode });
  pdfBtn.classList.add('view-mode-btn');
  if (!isWebMode) pdfBtn.classList.add('active');
  pdfBtn.setAttribute('title', 'PDF / paginated view');
  pdfBtn.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="8" y1="13" x2="16" y2="13"/>
      <line x1="8" y1="17" x2="13" y2="17"/>
    </svg>
    <span>PDF</span>
  `;

  // ── Production-view toggle ─────────────────────────────────────────
  // ASCII conveyor-belt / package icon: box sitting on rollers
  const prodBtn = document.createElement('a');
  prodBtn.href = buildViewUrl({ web: isWebMode ? '1' : '0', production: !isProdMode });
  prodBtn.classList.add('view-mode-btn', 'prod-toggle-btn');
  if (isProdMode) prodBtn.classList.add('active');
  prodBtn.setAttribute('title', isProdMode ? 'Production view (conditions resolved) — click to disable' : 'Preview view (conditions visible) — click to enable production view');
  prodBtn.innerHTML = `
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <!-- package / box on a conveyor -->
      <rect x="4" y="3" width="16" height="12" rx="1"/>
      <line x1="4" y1="8" x2="20" y2="8"/>
      <line x1="12" y1="3" x2="12" y2="8"/>
      <!-- conveyor surface -->
      <line x1="1" y1="18" x2="23" y2="18"/>
      <!-- rollers -->
      <circle cx="5"  cy="20" r="2"/>
      <circle cx="12" cy="20" r="2"/>
      <circle cx="19" cy="20" r="2"/>
    </svg>
    <span>Prod</span>
  `;

  row.appendChild(webBtn);
  row.appendChild(pdfBtn);
  row.appendChild(prodBtn);
  return row;
}

/**
 * Creates the expandable content area with TOC structure
 * 
 * @param {Array} chapterGroups - Grouped chapter data
 * @returns {Element} Expandable content element
 */
function createExpandableContent(chapterGroups, navData = {}) {
  const expandableContent = document.createElement("div");
  expandableContent.classList.add("hamburger-expandable-content");

  // Build collapsible TOC structure
  const tocList = createTocList(chapterGroups);
  expandableContent.appendChild(tocList);

  const { manuals = [], currentManualId, currentSiteId, context = 'default' } = navData;
  const isWebStaticContext = context === 'web-static';

  // ── Switch Manual section (not shown on web-static pages) ─────────
  if (!isWebStaticContext && manuals.length > 0 && currentSiteId) {
    const otherManuals = manuals.filter(
      m => String(m.siteId) === String(currentSiteId) &&
           String(m.id) !== String(currentManualId)
    );
    if (otherManuals.length > 0) {
      expandableContent.appendChild(
        createMenuSection("Switch Manual", otherManuals.map(m => ({
          label: m.userTitle || m.title,
          href: `/manual/${m.id}/siteId/${m.siteId}/`,
        })))
      );
    }
  }

  // ── Switch Language section removed ───────────────────────────────
  // Language switching is handled on the home page only.

  // ── Links section ─────────────────────────────────────────────────
  const pdfBase = (typeof process !== 'undefined' && process.env && process.env.GATSBY_PDF_BASE_URL) || '';
  const linkItems = [];
  if (pdfBase && currentManualId && currentSiteId) {
    linkItems.push({
      label: "Download PDF",
      href: `${pdfBase}/DCI_DigiGuide_Manual_${currentManualId}_Site_${currentSiteId}.pdf`,
      external: true,
    });
  }
  if (currentManualId) {
    linkItems.push({
      label: "Open in DigiGuide App",
      href: `dci-digiguide:///link/Manual?id=${currentManualId}`,
      external: false,
    });
    linkItems.push({
      label: "Share / Link page",
      href: `/link/Manual?id=${currentManualId}`,
      external: false,
    });
  }
  if (linkItems.length > 0) {
    expandableContent.appendChild(createMenuSection("Links", linkItems));
  }

  return expandableContent;
}

/**
 * Creates a collapsible menu section (Switch Manual, Switch Language, Links)
 *
 * @param {string} title - Section heading
 * @param {Array<{label, href, external}>} items - Link items
 * @returns {Element}
 */
function createMenuSection(title, items) {
  const section = document.createElement("div");
  section.classList.add("menu-section");

  // Toggle header
  const toggle = document.createElement("button");
  toggle.classList.add("menu-section-toggle");
  toggle.setAttribute("aria-expanded", "false");
  toggle.innerHTML = `
    <svg class="menu-section-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="6,9 12,15 18,9"></polyline>
    </svg>
    <span>${title}</span>
  `;

  // Content list
  const list = document.createElement("ul");
  list.classList.add("menu-section-list", "collapsed");
  items.forEach(({ label, href, external }) => {
    const li = document.createElement("li");
    const a = document.createElement("a");
    // Preview params are stored in sessionStorage — links stay clean.
    a.href = href;
    a.textContent = label;
    a.classList.add("menu-section-link");
    if (external) {
      a.target = "_blank";
      a.rel = "noreferrer";
    }
    li.appendChild(a);
    list.appendChild(li);
  });

  toggle.addEventListener("click", (e) => {
    e.preventDefault();
    if (document.activeElement === toggle) toggle.blur();
    const expanded = toggle.getAttribute("aria-expanded") === "true";
    toggle.setAttribute("aria-expanded", String(!expanded));
    list.classList.toggle("collapsed", expanded);
    toggle.classList.toggle("expanded", !expanded);
  });

  section.appendChild(toggle);
  section.appendChild(list);
  return section;
}

/**
 * Creates the TOC list structure
 *
 * @param {Array} chapterGroups - Grouped chapter data
 * @returns {Element} TOC list element
 */
function createTocList(chapterGroups) {
  const tocList = document.createElement('ul');
  tocList.classList.add('integrated-toc-list');
  
  // Build the TOC structure
  chapterGroups.forEach((chapterGroup, chapterIndex) => {
    const chapterLi = createChapterElement(chapterGroup);
    tocList.appendChild(chapterLi);
  });
  
  return tocList;
}

/**
 * Creates a chapter element with toggle and topics
 * 
 * @param {Object} chapterGroup - Chapter group data
 * @returns {Element} Chapter list item element
 */
function createChapterElement(chapterGroup) {
  const chapterLi = document.createElement('li');
  chapterLi.classList.add('toc-chapter');
  
  // Create chapter toggle section
  const chapterToggle = createChapterToggle(chapterGroup);
  
  // Create topics list
  const topicsUl = createTopicsList(chapterGroup.topics);
  
  chapterLi.appendChild(chapterToggle);
  chapterLi.appendChild(topicsUl);
  
  return chapterLi;
}

/**
 * Creates the chapter toggle section
 * 
 * @param {Object} chapterGroup - Chapter group data
 * @returns {Element} Chapter toggle element
 */
function createChapterToggle(chapterGroup) {
  const chapterToggle = document.createElement('div');
  chapterToggle.classList.add('toc-chapter-toggle');
  
  // Create expand/collapse button
  const expandBtn = document.createElement('button');
  expandBtn.classList.add('toc-expand-btn');
  expandBtn.setAttribute('aria-expanded', 'false');
  expandBtn.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="9,18 15,12 9,6"></polyline>
    </svg>
  `;
  
  // Create chapter link
  const chapterLink = document.createElement('span');
  chapterLink.classList.add('toc-chapter-link');
  // Add Tailwind classes for chapter styling
  chapterLink.classList.add('font-header', 'text-xl', 'text-gray-4', 'font-weight-normal');
  chapterLink.textContent = chapterGroup.heading.text;
  
  // Create chapter navigation arrow
  const chapterNavArrow = document.createElement('a');
  chapterNavArrow.href = `#${chapterGroup.heading.id}`;
  chapterNavArrow.classList.add('toc-nav-arrow');
  chapterNavArrow.setAttribute('aria-label', `Navigate to ${chapterGroup.heading.text}`);
  chapterNavArrow.innerHTML = `
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="9,18 15,12 9,6"></polyline>
    </svg>
  `;
  
  chapterToggle.appendChild(expandBtn);
  chapterToggle.appendChild(chapterLink);
  chapterToggle.appendChild(chapterNavArrow);
  
  return chapterToggle;
}

/**
 * Creates the topics list for a chapter
 * 
 * @param {Array} topics - Array of topic headings
 * @returns {Element} Topics list element
 */
function createTopicsList(topics) {
  const topicsUl = document.createElement('ul');
  topicsUl.classList.add('toc-topics', 'collapsed');
  
  topics.forEach(topicHeading => {
    const topicLi = createTopicElement(topicHeading);
    topicsUl.appendChild(topicLi);
  });
  
  return topicsUl;
}

/**
 * Creates a topic element
 * 
 * @param {Object} topicHeading - Topic heading data
 * @returns {Element} Topic list item element
 */
function createTopicElement(topicHeading) {
  const topicLi = document.createElement('li');
  topicLi.classList.add('toc-topic');
  
  // Create topic container
  const topicContainer = document.createElement('div');
  topicContainer.classList.add('toc-topic-container');
  
  const topicLink = document.createElement('a');
  topicLink.href = `#${topicHeading.id}`;
  topicLink.classList.add('toc-topic-link');
  // Add Tailwind classes for topic styling
  topicLink.classList.add('rich-text-content', 'text-grey-2', 'font-label', 'text-body', 'tracking-wide', 'block', 'uppercase');
  topicLink.textContent = topicHeading.text;
  
  // Create topic navigation arrow
  const topicNavArrow = document.createElement('a');
  topicNavArrow.href = `#${topicHeading.id}`;
  topicNavArrow.classList.add('toc-nav-arrow', 'toc-topic-nav-arrow');
  topicNavArrow.setAttribute('aria-label', `Navigate to ${topicHeading.text}`);
  topicNavArrow.innerHTML = `
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="9,18 15,12 9,6"></polyline>
    </svg>
  `;
  
  topicContainer.appendChild(topicLink);
  topicContainer.appendChild(topicNavArrow);
  topicLi.appendChild(topicContainer);
  
  return topicLi;
}

/**
 * Sets up event handlers for the hamburger menu
 *
 * @param {Element} hamburgerContainer - The hamburger menu container
 * @returns {Object} Control functions for the menu
 */
function setupHamburgerMenuEvents(hamburgerContainer) {
  const hamburgerBtn = hamburgerContainer.querySelector('.hamburger-toggle-btn');
  const expandableContent = hamburgerContainer.querySelector('.hamburger-expandable-content');

  let isExpanded = false;

  // Prevents the "close-on-outside-click" handler from firing immediately after a toggle.
  // This matters when an external React button calls hamburgerBtn.click(): the programmatic
  // click AND the React button's own native click both bubble to document; the second one
  // would otherwise see the menu as expanded and close it again right away.
  let justToggled = false;
  const markJustToggled = () => {
    justToggled = true;
    setTimeout(() => { justToggled = false; }, 100);
  };

  const toggleMenu = () => {
    isExpanded = !isExpanded;
    hamburgerContainer.classList.toggle('expanded', isExpanded);
    document.body.classList.toggle('hamburger-menu-open', isExpanded);

    // FIXED: Remove focus from the hamburger button after clicking
    // This prevents the button from staying highlighted
    if (hamburgerBtn && document.activeElement === hamburgerBtn) {
      hamburgerBtn.blur();
    }

    debugLog(`Hamburger menu ${isExpanded ? 'expanded' : 'collapsed'}`);
  };

  // Set up hamburger toggle button
  hamburgerBtn.addEventListener('click', (e) => {
    e.preventDefault(); // Prevent any default button behavior
    markJustToggled();
    toggleMenu();
  });

  // FIXED: Also handle mousedown to prevent focus on click
  hamburgerBtn.addEventListener('mousedown', (e) => {
    // Prevent the button from receiving focus when clicked with mouse
    e.preventDefault();
  });

  // Set up content area event handling
  setupContentAreaEvents(expandableContent, toggleMenu);

  // Global click handler — close when clicking outside.
  // Inlined here (not in setupGlobalEvents) so it closes over justToggled.
  document.addEventListener('click', (e) => {
    if (justToggled) return; // skip: we just toggled, avoid immediate re-close
    if (hamburgerContainer.classList.contains('expanded') &&
        !hamburgerContainer.contains(e.target)) {
      toggleMenu();
    }
  });

  // Handle escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && hamburgerContainer.classList.contains('expanded')) {
      toggleMenu();
    }
  });

  debugLog("Hamburger menu event handlers set up");

  return {
    container: hamburgerContainer,
    toggleMenu,
    isExpanded: () => isExpanded,
    destroy: () => {
      hamburgerContainer.remove();
      document.body.classList.remove('hamburger-menu-open');
      debugLog("Hamburger menu destroyed");
    }
  };
}

/**
 * Sets up event handlers for the content area
 * 
 * @param {Element} expandableContent - The expandable content element
 * @param {Function} toggleMenu - Function to toggle the menu
 */
function setupContentAreaEvents(expandableContent, toggleMenu) {
  expandableContent.addEventListener('click', (e) => {
    // Handle expand/collapse button clicks
    const expandBtn = e.target.closest('.toc-expand-btn');
    if (expandBtn) {
      handleExpandButtonClick(e, expandBtn);
      return;
    }
    
    // Handle chapter title clicks - expand/collapse instead of navigate
    const chapterLink = e.target.closest('.toc-chapter-link');
    if (chapterLink) {
      handleChapterLinkClick(e, chapterLink);
      return;
    }
    
    // Handle navigation arrow clicks (both chapter and topic)
    const navArrow = e.target.closest('.toc-nav-arrow');
    if (navArrow) {
      handleNavigationArrowClick(e, navArrow, toggleMenu);
      return;
    }
    
    // Handle TOC link clicks (topic links)
    const tocLink = e.target.closest('.toc-topic-link');
    if (tocLink) {
      handleTopicLinkClick(e, tocLink, toggleMenu);
    }
  });
}

/**
 * Handles expand button clicks
 * 
 * @param {Event} e - Click event
 * @param {Element} expandBtn - Expand button element
 */
function handleExpandButtonClick(e, expandBtn) {
  e.preventDefault();
  
  // FIXED: Remove focus from expand button after clicking
  if (document.activeElement === expandBtn) {
    expandBtn.blur();
  }
  
  const chapterLi = expandBtn.closest('.toc-chapter');
  const topicsUl = chapterLi.querySelector('.toc-topics');
  const isChapterExpanded = expandBtn.getAttribute('aria-expanded') === 'true';
  
  expandBtn.setAttribute('aria-expanded', !isChapterExpanded);
  topicsUl.classList.toggle('collapsed', isChapterExpanded);
  expandBtn.classList.toggle('expanded', !isChapterExpanded);
  
  debugLog(`Chapter ${isChapterExpanded ? 'collapsed' : 'expanded'}`);
}

/**
 * Handles chapter link clicks
 * 
 * @param {Event} e - Click event
 * @param {Element} chapterLink - Chapter link element
 */
function handleChapterLinkClick(e, chapterLink) {
  e.preventDefault();
  
  // FIXED: Remove focus from chapter link after clicking
  if (document.activeElement === chapterLink) {
    chapterLink.blur();
  }
  
  const chapterLi = chapterLink.closest('.toc-chapter');
  const expandBtn = chapterLi.querySelector('.toc-expand-btn');
  const topicsUl = chapterLi.querySelector('.toc-topics');
  const isChapterExpanded = expandBtn.getAttribute('aria-expanded') === 'true';
  
  expandBtn.setAttribute('aria-expanded', !isChapterExpanded);
  topicsUl.classList.toggle('collapsed', isChapterExpanded);
  expandBtn.classList.toggle('expanded', !isChapterExpanded);
  
  debugLog(`Chapter ${isChapterExpanded ? 'collapsed' : 'expanded'} via title click`);
}

/**
 * Handles navigation arrow clicks
 * 
 * @param {Event} e - Click event
 * @param {Element} navArrow - Navigation arrow element
 * @param {Function} toggleMenu - Function to toggle the menu
 */
function handleNavigationArrowClick(e, navArrow, toggleMenu) {
  e.preventDefault(); // Prevent default anchor behavior
  
  // FIXED: Remove focus from navigation arrow after clicking
  if (document.activeElement === navArrow) {
    navArrow.blur();
  }
  
  const targetId = navArrow.getAttribute('href').substring(1); // Remove the #
  const targetElement = document.getElementById(targetId);
  
  if (targetElement) {
    scrollToElement(targetElement, targetId);
  }
  
  // Close menu on mobile after clicking navigation arrow
  if (window.innerWidth <= 768) {
    toggleMenu();
  }
}

/**
 * Handles topic link clicks
 * 
 * @param {Event} e - Click event
 * @param {Element} tocLink - Topic link element
 * @param {Function} toggleMenu - Function to toggle the menu
 */
function handleTopicLinkClick(e, tocLink, toggleMenu) {
  e.preventDefault(); // Prevent default anchor behavior
  
  // FIXED: Remove focus from topic link after clicking
  if (document.activeElement === tocLink) {
    tocLink.blur();
  }
  
  const targetId = tocLink.getAttribute('href').substring(1); // Remove the #
  const targetElement = document.getElementById(targetId);
  
  if (targetElement) {
    scrollToElement(targetElement, targetId);
  }
  
  // Close menu on mobile after clicking link
  if (window.innerWidth <= 768) {
    toggleMenu();
  }
}

/**
 * Scrolls to a target element with offset
 * 
 * @param {Element} targetElement - Element to scroll to
 * @param {string} targetId - ID of the target element
 */
function scrollToElement(targetElement, targetId) {
  // Use the always-visible nav bar height from CSS var, plus a small margin
  const navH = parseInt(document.documentElement.style.getPropertyValue('--link-card-nav-height')) || 44;
  const offset = navH + 24;
  const elementPosition = targetElement.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;
  
  // Smooth scroll to position
  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
  
  // Update URL hash without jumping, then fire hashchange so listeners update
  if (window.history && window.history.pushState) {
    const oldURL = window.location.href;
    window.history.pushState(null, null, `#${targetId}`);
    const newURL = window.location.href;
    window.dispatchEvent(new HashChangeEvent('hashchange', { oldURL, newURL }));
  }

  debugLog(`Scrolled to element: ${targetId}`);
}

/**
 * Sets up global event handlers
 * 
 * @param {Element} hamburgerContainer - The hamburger menu container
 * @param {Function} toggleMenu - Function to toggle the menu
 */
function setupGlobalEvents(hamburgerContainer, toggleMenu) {
  // Close menu when clicking outside
  document.addEventListener('click', (e) => {
    if (hamburgerContainer.classList.contains('expanded') && 
        !hamburgerContainer.contains(e.target)) {
      toggleMenu();
    }
  });
  
  // Handle escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && hamburgerContainer.classList.contains('expanded')) {
      toggleMenu();
    }
  });
}

/**
 * Creates a hamburger menu specifically for paginated contexts
 * This version hides on print and works alongside paginated TOC
 * 
 * @param {Object} config - Configuration object
 * @returns {Object} Hamburger menu control object
 */
function createPaginatedHamburgerMenu(config) {
  debugLog("Creating hamburger menu for paginated context");
  
  return createHamburgerMenu({
    ...config,
    context: 'paginated'
  });
}

/**
 * Creates a hamburger menu specifically for non-paginated contexts
 * This version is the primary navigation method
 * 
 * @param {Object} config - Configuration object
 * @returns {Object} Hamburger menu control object
 */
function createPreviewHamburgerMenu(config) {
  debugLog("Creating hamburger menu for preview/non-paginated context");
  
  return createHamburgerMenu({
    ...config,
    context: 'preview'
  });
}

export default createHamburgerMenu;
export { 
  createPaginatedHamburgerMenu, 
  createPreviewHamburgerMenu,
  groupHeadingsByChapter,
  scrollToElement
};